import { createSelector } from "reselect"
import { get, chain, map, mapKeys, identity } from "lodash"
import { formatLabel, getTickerAndMicFromContributor } from "helpers/securities"
import { buildContributorKey } from "helpers/contributors"
import { computeTradeBuckets, filterAndOrderTrades, getTradeType } from "helpers/simulator"

const EMPTY_ARRAY = []

export const getTradeTypeDisplay = state => get(state, "modules.experimentTrades.tradeType")
export const getRawPortfolioComposition = state => get(state, "global.data.basePortfolioPositionDate.data.model.simulation.composition[0]", EMPTY_ARRAY)

const getPortfolioContributorsDollar = state => get(state, "global.data.basePortfolioPositionDate.data.model.simulation.composition[0].positions", EMPTY_ARRAY)
const getPortfolioContributorsPercent = state => get(state, "global.data.basePortfolioPositionDate.data.model.simulation.percent[0].positions", EMPTY_ARRAY)

const getExperimentDollar = state => get(state, "global.data.experimentDate.data.model.simulation.composition[0].positions", EMPTY_ARRAY)
const getExperimentPercent = state => get(state, "global.data.experimentDate.data.model.simulation.percent[0].positions", EMPTY_ARRAY)

function computeContributors(contributorsDollar, contributorsPercent) {
  return chain({})
    .merge(
      mapKeys(contributorsDollar, buildContributorKey),
      mapKeys(contributorsPercent, buildContributorKey),
    )
    .mapValues(contributorMap)
    .value()
}

function contributorMap(contributor) {
  const { ticker, mic } = getTickerAndMicFromContributor(contributor)
  return {
    key: buildContributorKey(contributor),
    label: formatLabel({ ticker, country: contributor.country }),
    mic,
    ticker,
    ...contributor,
    classification: get(contributor, "classification.name"),
    positionPercentEquity: contributor.positionPercentEquity / 100,
  }
}

const getPortfolioContributors = createSelector(
  [getPortfolioContributorsDollar, getPortfolioContributorsPercent],
  computeContributors,
)

const getExperimentContributors = createSelector(
  [getExperimentDollar, getExperimentPercent],
  computeContributors,
)

export const getPortfolioWorkingEquity = createSelector(
  [getRawPortfolioComposition],
  (portfolioComposition) => portfolioComposition.referenceEquity || portfolioComposition.modeledGmv,
)

export const getAllTrades = createSelector(
  [getPortfolioContributors, getExperimentContributors, getPortfolioWorkingEquity],
  (portfolioPositions, experimentPositions, portfolioWorkingEquity) => {
    const portfolio = map(portfolioPositions, (security, securityId) => {
      const experimentPosition = experimentPositions[securityId]
      const tradeEconomicExposure = experimentPosition ? experimentPosition.economicExposure - security.economicExposure : 0 - security.economicExposure
      const tradePercentDelta = tradeEconomicExposure / portfolioWorkingEquity

      return {
        ...security,
        securityPercentEquity: security.positionPercentEquity,
        tradePercentDelta,
        experimentSize: experimentPosition ? experimentPosition.economicExposure : 0,
        negative: tradePercentDelta < 0,
        tradeType: getTradeType({ securityPercentEquity: security.positionPercentEquity, tradePercentDelta }),
      }
    })
    // Add positions only in the experiment
    const experimentPortolio = chain(experimentPositions)
      .map((security, securityId) => {
        const portfolioPosition = get(portfolioPositions, securityId)
        if (!portfolioPosition) {
          const tradePercentDelta = get(security, "positionPercentEquity", 0)

          return {
            ...security,
            securityPercentEquity: 0,
            tradePercentDelta,
            experimentSize: security.economicExposure,
            negative: tradePercentDelta < 0,
            tradeType: getTradeType({ securityPercentEquity: 0, tradePercentDelta }),
          }
        } else {
          return null
        }
      })
      .filter(identity)
      .value()
    return [...portfolio, ...experimentPortolio]
  },
)

export const getTradedPositions = createSelector(
  [getAllTrades],
  filterAndOrderTrades,
)

export const tradesBuckets = createSelector(
  [getTradedPositions, getAllTrades],
  computeTradeBuckets,
)

export const filterTrades = createSelector(
  [tradesBuckets, getTradeTypeDisplay],
  (trades, tradeTypeDisplay) => trades[tradeTypeDisplay],
)
